/*
 * CliC — made-to-order cart-line pairing (RX_SPEC.md §14.5; CUSTOM_LENS_SPEC.md §9.1)
 *
 * Keeps the two cart lines of a made-to-order lens order in sync. The Lens Flow
 * wizard adds a prescription as TWO lines — a frame variant + a lens variant — that
 * share a hidden `_rx_group` line-item property (and an `_rx_role` of "frame" /
 * "lens"). The Custom Lenses path (2026-08-26) does the same with `_custom_group` /
 * `_custom_role`. Both pair kinds are handled here, keyed separately (a group id is
 * namespaced by its property name, so an Rx and a Custom pair can never merge).
 * This script enforces, on every cart change:
 *   - remove one line of a group  ->  its partner is removed too
 *   - change the quantity of one  ->  the partner's quantity is mirrored
 *
 * It works purely off the Cart AJAX API (/cart.js + /cart/update.js) and the
 * documented `theme:cart:*` events — no dependency on the (minified) theme cart
 * internals. Fail-safe: a cart with no `_rx_group` / `_custom_group` lines (or an
 * unreadable /cart.js) is a silent no-op, so this can ship globally without risk.
 *
 * Scope note: the in-wizard Rx flow is Palo Alto-only (RX_SPEC §1); the live
 * Canopy theme is read-only and not in this repo, so pairing there is out of reach
 * (and unneeded — Canopy is being replaced by this theme at cutover).
 */
(function () {
  "use strict";

  // One entry per made-to-order pair kind: the hidden group prop + its role prop.
  var PAIR_KINDS = [
    { group: "_rx_group", role: "_rx_role" },
    { group: "_custom_group", role: "_custom_role" },
  ];
  var lastKnownQty = {}; // "<groupProp>:<groupId>" -> last balanced quantity (drives sync direction)
  var reconciling = false; // re-entrancy guard
  var pending = false; // a reconcile was requested while one was running
  var RELOAD_FLAG = "clic_rx_pairing_reloaded";

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (r) {
      return r.ok ? r.json() : null;
    });
  }

  // Group cart items by pair kind + group id. Returns
  // { "<groupProp>:<groupId>": { items: [{key, quantity, role}] } }.
  function groupRxLines(cart) {
    var groups = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var props = it.properties || {};
      for (var k = 0; k < PAIR_KINDS.length; k++) {
        var kind = PAIR_KINDS[k];
        var g = props[kind.group];
        if (!g) continue;
        var id = kind.group + ":" + g;
        if (!groups[id]) groups[id] = { items: [] };
        groups[id].items.push({ key: it.key, quantity: it.quantity, role: props[kind.role] || "" });
      }
    }
    return groups;
  }

  // Compute { updates: {key: qty}, changed: bool } needed to balance every group.
  function planUpdates(groups) {
    var updates = {};
    var changed = false;
    var present = {};
    Object.keys(groups).forEach(function (g) {
      present[g] = true;
      var lines = groups[g].items;

      // Orphan (partner already removed) -> remove the remaining line(s).
      if (lines.length < 2) {
        for (var i = 0; i < lines.length; i++) {
          if (lines[i].quantity !== 0) { updates[lines[i].key] = 0; changed = true; }
        }
        delete lastKnownQty[g];
        return;
      }

      // 2+ lines: enforce a single quantity across the group.
      var qtys = lines.map(function (l) { return l.quantity; });
      var allEqual = qtys.every(function (q) { return q === qtys[0]; });
      if (allEqual) { lastKnownQty[g] = qtys[0]; return; }

      // Mismatch -> the line that still equals the last balanced qty is the
      // unchanged one; the other is what the shopper just changed. If we can't
      // tell (no history, or both changed), fall back to the larger quantity.
      var prev = lastKnownQty[g];
      var target;
      if (prev != null) {
        var changedLines = lines.filter(function (l) { return l.quantity !== prev; });
        target = changedLines.length === 1 ? changedLines[0].quantity : Math.max.apply(null, qtys);
      } else {
        target = Math.max.apply(null, qtys);
      }
      if (target < 1) target = 1; // a 0 target would be a removal — handled by the orphan branch next pass

      for (var j = 0; j < lines.length; j++) {
        if (lines[j].quantity !== target) { updates[lines[j].key] = target; changed = true; }
      }
      lastKnownQty[g] = target;
    });

    // Forget groups that are no longer in the cart.
    Object.keys(lastKnownQty).forEach(function (g) { if (!present[g]) delete lastKnownQty[g]; });
    return { updates: updates, changed: changed };
  }

  function applyUpdates(updates) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) { return r.ok ? r.json() : null; });
  }

  function clearReloadGuard() {
    try { sessionStorage.removeItem(RELOAD_FLAG); } catch (e) { /* ignore */ }
  }

  function reconcile() {
    if (reconciling) { pending = true; return; }
    reconciling = true;
    getCart()
      .then(function (cart) {
        if (!cart) return null;
        var groups = groupRxLines(cart);
        if (Object.keys(groups).length === 0) { clearReloadGuard(); return null; }
        var plan = planUpdates(groups);
        if (!plan.changed) { clearReloadGuard(); return null; }
        return applyUpdates(plan.updates);
      })
      .then(function (updatedCart) {
        reconciling = false;
        if (updatedCart) afterCorrection();
        if (pending) { pending = false; reconcile(); }
      })
      .catch(function () {
        reconciling = false;
        if (pending) { pending = false; reconcile(); }
      });
  }

  // After we change the cart, make the theme re-render. The drawer listens for
  // theme:cart:refresh (the same event the Lens Flow wizard fires). On a plain
  // cart page with no AJAX drawer, fall back to a single guarded reload so the
  // removed partner disappears from view.
  function afterCorrection() {
    try { document.dispatchEvent(new CustomEvent("theme:cart:refresh")); } catch (e) { /* ignore */ }
    var hasDrawer = document.querySelector("cart-element, [data-cart-drawer]");
    if (hasDrawer) return;
    var onCartPage = document.body && document.body.classList.contains("template-cart");
    if (!onCartPage) return;
    try {
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, "1"); // cleared once reconcile sees a balanced cart
        window.location.reload();
      }
    } catch (e) { /* ignore */ }
  }

  var debounceTimer = null;
  function scheduleReconcile() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 120);
  }

  // 1) Theme cart events (drawer + ajax cart) — the primary trigger.
  ["theme:cart:update", "theme:cart:added", "theme:cart:refresh", "theme:cart:loaded"].forEach(
    function (ev) { document.addEventListener(ev, scheduleReconcile); },
  );

  // 2) Belt-and-suspenders: observe cart-mutating fetches we didn't originate.
  //    (Skip our own /cart/update.js so corrections don't trigger themselves.)
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
      var p = origFetch.apply(this, arguments);
      if (isCartMutation && url.indexOf("/cart/update.js") === -1) {
        p.then(function () { scheduleReconcile(); }).catch(function () {});
      }
      return p;
    };
  }

  // 3) On load — catches a no-JS remove (full navigation on the cart page).
  function boot() { reconcile(); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
